/**
 * Contexto de autenticación.
 *
 * El token se guarda en sessionStorage: se borra al cerrar la pestaña, lo
 * que reduce el daño en caso de XSS persistente (no sobrevive entre sesiones
 * del navegador) a costa de exigir login al reabrir.
 *
 * Todas las llamadas autenticadas pasan por `authFetch`, que añade
 * Authorization: Bearer <token> y, ante un 401, limpia la sesión y redirige
 * a /login.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

const API_URL: string = import.meta.env.VITE_API_URL ?? "";
const TOKEN_KEY = "auth_token";

export type Role = "admin" | "user" | "comercial";

export interface AuthUser {
  id: string;
  email: string;
  role: Role;
}

/** Página inicial según el rol: el comercial arranca en Comisiones. */
export function landingPathFor(role: Role): string {
  return role === "comercial" ? "/comisiones" : "/";
}

interface AuthState {
  user: AuthUser | null;
  status: "loading" | "anon" | "authed";
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
}

const Ctx = createContext<AuthContextValue | null>(null);

function getToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function setToken(token: string | null): void {
  try {
    if (token == null) sessionStorage.removeItem(TOKEN_KEY);
    else sessionStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* noop */
  }
}

/**
 * Wrapper de fetch que añade Authorization. Si la respuesta es 401, borra
 * el token y lanza un error específico para que el guard redirija a /login.
 */
export async function authFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const token = getToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  // FormData necesita que el navegador genere el boundary multipart por sí
  // mismo, así que NO le ponemos Content-Type por defecto.
  if (init.body != null && !headers.has("Content-Type") && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  const url = input.startsWith("http") ? input : `${API_URL}${input}`;
  const r = await fetch(url, { ...init, headers });
  if (r.status === 401) {
    setToken(null);
    // Disparo un evento para que el contexto reaccione (logout suave).
    window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    throw new Error("Sesión expirada. Inicia sesión de nuevo.");
  }
  return r;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => ({
    user: null,
    status: getToken() ? "loading" : "anon",
  }));

  const refreshMe = useCallback(async () => {
    if (!getToken()) {
      setState({ user: null, status: "anon" });
      return;
    }
    try {
      const r = await authFetch("/api/auth/me");
      if (!r.ok) {
        setState({ user: null, status: "anon" });
        return;
      }
      const data = (await r.json()) as { user: AuthUser };
      setState({ user: data.user, status: "authed" });
    } catch {
      setState({ user: null, status: "anon" });
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const r = await fetch(`${API_URL}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!r.ok) {
      const err = (await r.json().catch(() => ({}))) as { error?: string };
      throw new Error(err.error ?? `HTTP ${r.status}`);
    }
    const data = (await r.json()) as { token: string; user: AuthUser };
    setToken(data.token);
    setState({ user: data.user, status: "authed" });
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authFetch("/api/auth/logout", { method: "POST" });
    } catch {
      /* ignore */
    }
    setToken(null);
    setState({ user: null, status: "anon" });
  }, []);

  useEffect(() => {
    void refreshMe();
  }, [refreshMe]);

  useEffect(() => {
    const handler = () => {
      setToken(null);
      setState({ user: null, status: "anon" });
    };
    window.addEventListener("auth:unauthorized", handler);
    return () => window.removeEventListener("auth:unauthorized", handler);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout, refreshMe }),
    [state, login, logout, refreshMe],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth fuera de <AuthProvider>");
  return v;
}
